import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpGatewayService } from '../../global/services/http-gateway.service';
import { Role, UserContract } from '../../global/store/authentication/types';
import {
  CreateTaskContract,
  TaskContract,
  TaskCountsContract,
  TaskScope,
  TaskStatusFilter,
  UpdateTaskContract,
} from '../store/tasks/types';

@Injectable({ providedIn: 'root' })
export class TasksApi {
  private httpService = inject(HttpGatewayService);

  private clusterBase(salesOrganizationId: string, clusterId: string): string {
    return `/sales-organizations/${salesOrganizationId}/tasks/clusters/${clusterId}`;
  }

  public listTasks(
    salesOrganizationId: string,
    clusterId: string,
    scope: TaskScope,
    statusFilter: TaskStatusFilter,
    userId?: string,
  ): Observable<TaskContract[]> {
    let params = new HttpParams();
    params = params.set('scope', scope);
    params = params.set('status', statusFilter);
    if (userId) params = params.set('userId', userId);
    return this.httpService.get<TaskContract[]>(
      this.clusterBase(salesOrganizationId, clusterId),
      params,
    );
  }

  public listTasksForAccount(
    salesOrganizationId: string,
    clusterId: string,
    accountId: string,
    statusFilter: TaskStatusFilter,
  ): Observable<TaskContract[]> {
    let params = new HttpParams();
    params = params.set('accountId', accountId);
    params = params.set('status', statusFilter);
    return this.httpService.get<TaskContract[]>(
      this.clusterBase(salesOrganizationId, clusterId),
      params,
    );
  }

  public getCounts(
    salesOrganizationId: string,
    clusterId: string,
    userId?: string,
  ): Observable<TaskCountsContract> {
    let params = new HttpParams();
    if (userId) params = params.set('userId', userId);
    return this.httpService.get<TaskCountsContract>(
      `${this.clusterBase(salesOrganizationId, clusterId)}/counts`,
      params,
    );
  }

  public getOrgCounts(
    salesOrganizationId: string,
  ): Observable<{ totalOpen: number }> {
    return this.httpService.get<{ totalOpen: number }>(
      `/sales-organizations/${salesOrganizationId}/tasks/counts`,
    );
  }

  public createTask(
    salesOrganizationId: string,
    clusterId: string,
    contract: CreateTaskContract,
  ): Observable<TaskContract> {
    return this.httpService.post<TaskContract>(
      this.clusterBase(salesOrganizationId, clusterId),
      contract,
    );
  }

  public updateTask(
    salesOrganizationId: string,
    clusterId: string,
    id: string,
    contract: UpdateTaskContract,
  ): Observable<TaskContract> {
    return this.httpService.put<TaskContract>(
      `${this.clusterBase(salesOrganizationId, clusterId)}/${id}`,
      contract,
    );
  }

  public deleteTask(
    salesOrganizationId: string,
    clusterId: string,
    id: string,
  ): Observable<void> {
    return this.httpService.delete<void>(
      `${this.clusterBase(salesOrganizationId, clusterId)}/${id}`,
    );
  }

  public getClusterMembers(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<UserContract[]> {
    return this.httpService.get<UserContract[]>(
      `/users/sales-organizations/${salesOrganizationId}/clusters/${clusterId}/members`,
    );
  }

  public getOrgUsers(
    salesOrganizationId: string,
    roles?: Role[],
  ): Observable<UserContract[]> {
    let queryParams = new HttpParams();
    if (roles?.length) queryParams = queryParams.set('roles', roles.join(','));
    return this.httpService.get<UserContract[]>(
      `/users/sales-organizations/${salesOrganizationId}/users`,
      queryParams,
    );
  }
}
